import React from "react";
import {
  CheckCircle2,
  Circle,
  ArrowRight,
  Save,
  FileText,
  Wallet,
  FileSearch,
  Home,
  Send,
  Trash2,
  TriangleAlert,
  LoaderCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./DashboardDraftProgressCard.css";
import { validateDossierStepCompletion } from "../dossier/steps/dossierCompletionValidation";
const BACKEND_STEP_KEY_MAP = Object.freeze({
  PERSONAL: "personal-info",
  FINANCIAL: "financial-info",
  DOCUMENTS: "documents",
  EXTRACTION: "documents",
  PROPERTY: "property-information",
  REVIEW: "review-download",
});
function resolveDraftStepKey(draftMeta, draftData) {
  const candidate =
    draftMeta?.current_step_code ||
    draftMeta?.current_stage ||
    draftData?.current_step_code ||
    draftData?.current_stage ||
    draftMeta?.current_step_key ||
    "personal-info";
  const text = String(candidate).trim();
  if (STEP_ICON_MAP[text]) return text;
  return BACKEND_STEP_KEY_MAP[text.toUpperCase()] || "personal-info";
}
const STEP_ICON_MAP = {
  "personal-info": FileText,
  "financial-info": Wallet,
  documents: FileSearch,
  "property-information": Home,
  "review-download": Send,
};
function isFilled(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}
function isPersonalInfoStepComplete(personal, { mode = "primary" } = {}) {
  const baseComplete =
    isFilled(personal.first_name) &&
    isFilled(personal.last_name) &&
    isFilled(personal.date_of_birth) &&
    isFilled(personal.email);
  if (mode === "coBorrower") {
    return baseComplete;
  }
  return (
    baseComplete &&
    isFilled(personal.nationality) &&
    isFilled(personal.country_of_residence) &&
    isFilled(personal.residency_status) &&
    isFilled(personal.address) &&
    isFilled(personal.phone) &&
    isFilled(personal.marital_status) &&
    isFilled(personal.dependents) &&
    (isFilled(personal.disability) || personal.disability === 0) &&
    isFilled(personal.preferred_contact_method)
  );
}
function normalizeCountryCode(value) {
  const text = String(value || "")
    .trim()
    .toUpperCase();
  if (["ES", "SPAIN", "ESPAÑA", "ESPANA"].includes(text)) {
    return "ES";
  }
  if (["BE", "BELGIUM", "BELGIQUE"].includes(text)) {
    return "BE";
  }
  return text;
}
export default function DashboardDraftProgressCard({
  draftMeta,
  draftData,
  onContinue,
  onDelete,
  isDeleting = false,
}) {
  const { t } = useTranslation("homebuyer-dashboard-draft");
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");
  const steps = React.useMemo(() => {
    const checks = validateDossierStepCompletion(draftData || {});
    const items = [
      ["personal-info", "personalInfo"],
      ["financial-info", "financialInfo"],
      ["documents", "documents"],
      ["property-information", "propertyInformation"],
      ["review-download", "reviewDownload"],
    ];
    return items.map(([key, translationKey]) => ({
      key,
      label: t(`steps.${translationKey}`),
      requiredHint: t(`hints.${translationKey}`),
      completed: draftMeta?.progress_revalidating !== true && checks[key]?.isValid === true,
    }));
  }, [draftData, draftMeta?.progress_revalidating, t]);
  const awaitingServerProgress =
    draftMeta?.progress_source === "verified" &&
    (draftMeta?.progress_revalidating === true || !draftData?.borrowers?.length);
  const completedCount = awaitingServerProgress
    ? null
    : steps.filter((step) => step.completed).length;
  const currentStepKey = resolveDraftStepKey(draftMeta, draftData);
  // Resolve labels from the translated step list, not stale English metadata.
  const currentStepLabel =
    steps.find((step) => step.key === currentStepKey)?.label ||
    draftMeta?.current_step_label ||
    t("buttons.continue");
  const updatedAt = draftMeta?.updated_at
    ? new Date(draftMeta.updated_at).toLocaleString()
    : null;
  async function handleConfirmDelete() {
    if (!onDelete || isDeleting) {
      return;
    }
    try {
      setDeleteError("");
      await onDelete();
      setDeleteModalOpen(false);
    } catch (error) {
      setDeleteError(
        error?.message || t("delete.error", "Could not delete the draft."),
      );
    }
  }
  return (
    <>
      <div className="hb-draft-progress-card">
        <div className="hb-draft-progress-card__header">
          <div>
            <p className="hb-draft-progress-card__eyebrow">
              {t("header.eyebrow")}
            </p>
            <h3 className="hb-draft-progress-card__title">
              {currentStepLabel}
            </h3>
            <p className="hb-draft-progress-card__subtitle">
              {updatedAt
                ? t("header.updatedAt", {
                    date: updatedAt,
                  })
                : t("header.saved")}
            </p>
          </div>
          <div className="hb-draft-progress-card__status">
            <Save size={16} />
            <span>{t("status.draft")}</span>
          </div>
        </div>
        <div className="hb-draft-progress-card__summary">
          <div className="hb-draft-progress-card__summary-main">
            <span className="hb-draft-progress-card__summary-number">
              {completedCount === null ? "—" : completedCount}/{steps.length}
            </span>
            <span className="hb-draft-progress-card__summary-text">
              {awaitingServerProgress
                ? t("summary.loadingProgress", "Loading saved progress...")
                : t("summary.stepsCompleted")}
            </span>
          </div>
          <div className="hb-draft-progress-card__progress">
            <div
              className="hb-draft-progress-card__progress-bar"
              style={{
                width: `${((completedCount ?? 0) / steps.length) * 100}%`,
              }}
            />
          </div>
        </div>
        <div className="hb-draft-progress-card__steps">
          {steps.map((step) => {
            const Icon = STEP_ICON_MAP[step.key] || FileText;
            const isCurrent = step.key === currentStepKey;
            return (
              <div
                key={step.key}
                className={`hb-draft-progress-card__step ${
                  step.completed && !awaitingServerProgress ? "is-completed" : ""
                } ${isCurrent ? "is-current" : ""}`}
              >
                <div className="hb-draft-progress-card__step-left">
                  <div className="hb-draft-progress-card__step-icon">
                    {step.completed && !awaitingServerProgress ? (
                      <CheckCircle2 size={18} />
                    ) : (
                      <Icon size={18} />
                    )}
                  </div>
                  <div className="hb-draft-progress-card__step-text">
                    <div className="hb-draft-progress-card__step-title-row">
                      <span className="hb-draft-progress-card__step-title">
                        {step.label}
                      </span>
                      {isCurrent && (
                        <span className="hb-draft-progress-card__current-badge">
                          {t("states.current")}
                        </span>
                      )}
                    </div>
                    <span className="hb-draft-progress-card__step-hint">
                      {step.completed && !awaitingServerProgress
                        ? t("states.completed")
                        : step.requiredHint}
                    </span>
                  </div>
                </div>
                <div className="hb-draft-progress-card__step-right">
                  {step.completed && !awaitingServerProgress ? (
                    <span className="hb-draft-progress-card__done-label">
                      {t("states.done")}
                    </span>
                  ) : (
                    <Circle size={16} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="hb-draft-progress-card__footer">
          <button
            type="button"
            className="hb-draft-progress-card__button"
            onClick={onContinue}
            disabled={isDeleting}
          >
            {t("buttons.continue")}
            <ArrowRight size={16} />
          </button>
          {onDelete && (
            <button
              type="button"
              className="hb-draft-progress-card__delete-button"
              onClick={() => {
                setDeleteError("");
                setDeleteModalOpen(true);
              }}
              disabled={isDeleting}
            >
              <Trash2 size={15} />
              <span>{t("buttons.deleteDraft", "Delete draft")}</span>
            </button>
          )}
        </div>
      </div>
      {deleteModalOpen && (
        <div
          className="hb-draft-delete-modal__backdrop"
          onClick={() => {
            if (!isDeleting) {
              setDeleteModalOpen(false);
            }
          }}
        >
          <div
            className="hb-draft-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hb-draft-delete-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-draft-delete-modal__icon">
              <TriangleAlert size={24} />
            </div>
            <h3
              id="hb-draft-delete-modal-title"
              className="hb-draft-delete-modal__title"
            >
              {t("delete.title", "Delete this dossier draft?")}
            </h3>
            <p className="hb-draft-delete-modal__description">
              {t(
                "delete.description",
                "All information entered in this dossier draft will be removed. This action cannot be undone.",
              )}
            </p>
            {deleteError && (
              <div className="hb-draft-delete-modal__error">{deleteError}</div>
            )}
            <div className="hb-draft-delete-modal__actions">
              <button
                type="button"
                className="hb-draft-delete-modal__button hb-draft-delete-modal__button--cancel"
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                {t("delete.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="hb-draft-delete-modal__button hb-draft-delete-modal__button--delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <LoaderCircle
                      size={16}
                      className="hb-draft-delete-modal__spinner"
                    />
                    {t("delete.deleting", "Deleting...")}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    {t("delete.confirm", "Delete draft")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
